import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import blogService from '../services/blogService';
import { getImageUrl } from '../utils/imageUrl';
import { FiSearch, FiX, FiLoader, FiAlertCircle, FiMaximize2, FiArrowLeft } from 'react-icons/fi';
import { useLightbox } from '../context/LightboxContext';
import '../components/home/BlogSection.css';
import './ProductsPage.css';
import './BlogListPage.css';

const LIMIT = 12;

const BlogListPage = () => {
    const navigate = useNavigate();
    const [blogs, setBlogs] = useState([]);
    const { openLightbox } = useLightbox();
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [loadingInit, setLoadingInit] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState('');

    const sentinelRef = useRef(null);
    const debounceRef = useRef(null);

    const fetchBlogs = useCallback(
        async (pageNum, append = false) => {
            try {
                if (append) setLoadingMore(true);
                else setLoadingInit(true);
                setError('');

                const { blogs: newBlogs, pagination } = await blogService.getAll(
                    pageNum,
                    LIMIT,
                    search
                );

                setBlogs((prev) => (append ? [...prev, ...newBlogs] : newBlogs));
                setHasMore(pagination.hasNextPage || false);
                setTotalCount(pagination.totalCount || 0);
                setPage(pageNum);
            } catch (err) {
                setError(err.message || 'Failed to load posts. Please try again.');
            } finally {
                setLoadingInit(false);
                setLoadingMore(false);
            }
        },
        [search]
    );

    useEffect(() => {
        fetchBlogs(1, false);
    }, [fetchBlogs]);

    useEffect(() => {
        if (!sentinelRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore && !loadingInit) {
                    fetchBlogs(page + 1, true);
                }
            },
            { rootMargin: '200px' }
        );

        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [hasMore, loadingMore, loadingInit, page, fetchBlogs]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchInput(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSearch(val.trim());
        }, 600);
    };

    const clearSearch = () => {
        setSearchInput('');
        setSearch('');
        clearTimeout(debounceRef.current);
    };

    return (
        <div className="blog-list-page">
            <div className="page-header">
                <div className="container">
                    <div style={{ marginBottom: '0.85rem' }}>
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="product-page-back-btn"
                            aria-label="Go back"
                        >
                            <FiArrowLeft /> Back
                        </button>
                    </div>
                    <span className="section-eyebrow" style={{ color: '#FDBA74', marginBottom: '0.4rem', display: 'inline-block' }}>OFFICIAL BUYING GUIDES</span>
                    <h1>Blogs &amp; Appliance Guides</h1>
                    <p>Expert buying advice, genuine brand warranties, and smart installment planning from Rahber Corporation specialists.</p>
                </div>
            </div>

            <div className="container blog-list-inner">
                <div className="products-toolbar">
                    <div className="search-wrapper">
                        <FiSearch className="search-icon" />
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search posts…"
                            value={searchInput}
                            onChange={handleSearchChange}
                            aria-label="Search blog posts"
                        />
                        {searchInput && (
                            <button
                                type="button"
                                className="search-clear"
                                onClick={clearSearch}
                                title="Clear search"
                            >
                                <FiX />
                            </button>
                        )}
                    </div>
                    <div className="toolbar-right">
                        {!loadingInit && (
                            <p className="results-count">
                                {totalCount} post{totalCount !== 1 ? 's' : ''}
                            </p>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="products-error">
                        <FiAlertCircle />
                        <span>{error}</span>
                    </div>
                )}

                {loadingInit ? (
                    <div className="blogs-grid">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="blog-card blog-card-skeleton" />
                        ))}
                    </div>
                ) : blogs.length === 0 ? (
                    <div className="products-empty">
                        <FiAlertCircle className="empty-icon" />
                        <p>No posts found.</p>
                        {search && (
                            <button type="button" className="btn btn-outline" onClick={clearSearch}>
                                Clear search
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="blogs-grid">
                            {blogs.map((blog) => {
                                const img = getImageUrl(blog.featured_image || blog.image);
                                return (
                                    <article key={blog._id} className="blog-card">
                                        <div className="blog-image-wrap">
                                            <Link
                                                to={`/blogs/${encodeURIComponent(blog.slug)}`}
                                                className="blog-image"
                                            >
                                                {img ? (
                                                    <img
                                                        src={img}
                                                        alt={blog.title}
                                                        loading="lazy"
                                                        onError={(e) => {
                                                            e.currentTarget.src = 'https://images.unsplash.com/photo-1584568694244-14fbdf83bd30?w=900&auto=format&fit=crop';
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="blog-image-placeholder" />
                                                )}
                                            </Link>
                                            {img && (
                                                <button
                                                    type="button"
                                                    className="blog-zoom-btn"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        openLightbox(img, blog.title, blog.title);
                                                    }}
                                                    title="Tap to enlarge photo"
                                                    aria-label="Enlarge image"
                                                >
                                                    <FiMaximize2 />
                                                </button>
                                            )}
                                        </div>
                                        <div className="blog-content">
                                            <div className="blog-meta">
                                                <span className="blog-category">
                                                    {blog.category || 'Buying Guide'}
                                                </span>
                                            </div>
                                            <Link to={`/blogs/${encodeURIComponent(blog.slug)}`} className="blog-title-link">
                                                <h2 className="blog-title">{blog.title}</h2>
                                            </Link>
                                            <p className="blog-excerpt">{blog.excerpt}</p>
                                            <Link
                                                to={`/blogs/${encodeURIComponent(blog.slug)}`}
                                                className="blog-link"
                                            >
                                                Read more →
                                            </Link>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>

                        <div ref={sentinelRef} className="scroll-sentinel" />

                        {loadingMore && (
                            <div className="load-more-spinner">
                                <FiLoader className="spin-icon" />
                                <span>Loading more posts…</span>
                            </div>
                        )}

                        {!hasMore && !loadingMore && blogs.length > 0 && (
                            <p className="end-of-list">All {totalCount} posts loaded</p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default BlogListPage;
